import { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import type { DiagnosticNote, CodeSuggestion } from '../../types/codingAssistant';
import {
  createDiagnosticNote,
  getDiagnosticNotes,
  updateDiagnosticNote,
  searchIcd10Codes,
  reviewDiagnosticCode
} from '../../services/codingAssistant';
import { isSubscriptionError } from '../../services/api';
import { RichTextEditor } from './RichTextEditor';
import { QuickDictationRecorder } from './QuickDictationRecorder';
import { UnifiedCodeList } from './UnifiedCodeCard';

/**
 * Per AMP-29: the Diagnostic Notes free-text editor (rich-text editor, dictate
 * button, save button, last-updated timestamp) is hidden. The ICD-10 search
 * and approved-codes display remain. Backend rejects richText/plainText writes
 * regardless of this flag. Flip to `true` to restore the editor surfaces.
 */
const DIAGNOSTIC_NOTES_EDITOR_VISIBLE = false;

interface Props {
  appointmentId?: string;
  encounterId?: string;
  isEncounterMode?: boolean;
}

export const DiagnosticNotesSection: React.FC<Props> = ({ appointmentId, encounterId, isEncounterMode = false }) => {
  const [diagnosticNotes, setDiagnosticNotes] = useState<DiagnosticNote[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<CodeSuggestion[]>([]);
  const [isDictationOpen, setIsDictationOpen] = useState<Record<string, boolean>>({});
  const [expandedNotes, setExpandedNotes] = useState<Record<string, boolean>>({});
  // Synchronous in-flight guard: blocks concurrent handleSearchCodes calls
  // (e.g. rapid Enter presses) from dispatching duplicate POST /diagnostic-notes
  // creates before the first one resolves. useState would lag a render.
  const searchInFlightRef = useRef(false);
  // Same guard for approve: the backend appends the code without a duplicate
  // check, so a double-click or a second approve while one is saving must be
  // blocked. The state drives the spinner and the disabled search/card actions.
  const approveInFlightRef = useRef(false);
  const [approvingCodeId, setApprovingCodeId] = useState<string | null>(null);

  const loadDiagnosticNotes = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getDiagnosticNotes(appointmentId, encounterId, isEncounterMode);

      // Handle response structure
      if (response && response.data && response.data.diagnosticNotes && Array.isArray(response.data.diagnosticNotes)) {
        setDiagnosticNotes(response.data.diagnosticNotes);

        // Auto-expand all notes
        const expanded: Record<string, boolean> = {};
        response.data.diagnosticNotes.forEach((note: DiagnosticNote) => {
          expanded[note.id] = true;
        });
        setExpandedNotes(expanded);
      } else {
        setDiagnosticNotes([]);
      }
    } catch (error) {
      if (!isSubscriptionError(error)) {
        console.error('Failed to load diagnostic notes', error);
      }
      setDiagnosticNotes([]);
    } finally {
      setIsLoading(false);
    }
  }, [appointmentId, encounterId, isEncounterMode]);

  useEffect(() => {
    if (appointmentId || (isEncounterMode && encounterId)) {
      loadDiagnosticNotes();
    }
  }, [appointmentId, encounterId, isEncounterMode, loadDiagnosticNotes]);

  // Hidden per AMP-29; editor may return later.
  const handleSaveNote = async (noteId: string, richText: string, plainText: string) => {
    try {
      const response = await updateDiagnosticNote(appointmentId, encounterId, isEncounterMode, noteId, richText, plainText);

      // Update note in list
      setDiagnosticNotes(notes =>
        notes.map(n => n.id === noteId ? response.data.diagnosticNote : n)
      );

      toast.success('Diagnostic note saved');
    } catch (error) {
      if (!isSubscriptionError(error)) {
        toast.error('Failed to save diagnostic note');
      }
    }
  };

  // Hidden per AMP-29; editor may return later.
  const handleDictation = async (noteId: string, transcription: string) => {
    const note = diagnosticNotes.find(n => n.id === noteId);
    if (!note) return;

    const updatedContent = note.content ? `${note.content}<p>${transcription}</p>` : `<p>${transcription}</p>`;
    const updatedPlainText = note.plainText ? `${note.plainText}\n\n${transcription}` : transcription;

    await handleSaveNote(noteId, updatedContent, updatedPlainText);
    setIsDictationOpen({ ...isDictationOpen, [noteId]: false });
  };

  const handleSearchCodes = async () => {
    // Block re-entry while a previous create-then-search is still in flight.
    // The synchronous ref read protects against rapid Enter-key presses, which
    // bypass the button's `disabled={isSearching}` gate.
    if (searchInFlightRef.current) return;

    if (!searchQuery.trim()) {
      toast.error('Please enter a search term');
      return;
    }

    searchInFlightRef.current = true;
    try {
      // Lazy-create the diagnostic-note record on first search so the backend
      // has an id to attach approved codes to. The note is a persistence detail
      // post-AMP-29; the user never explicitly creates it.
      let note = diagnosticNotes[0];
      if (!note) {
        try {
          const response = await createDiagnosticNote(
            appointmentId,
            encounterId,
            isEncounterMode,
            '',
            '',
            undefined
          );
          note = response.data.diagnosticNote;
          setDiagnosticNotes([note]);
        } catch (error) {
          if (!isSubscriptionError(error)) {
            toast.error('Failed to start diagnostic-code record');
          }
          return;
        }
      }

      try {
        setIsSearching(true);
        const response = await searchIcd10Codes(appointmentId, encounterId, isEncounterMode, note.id, searchQuery);

        const codes = response.data.codes.filter((c: CodeSuggestion) => !/^IMO\d/i.test(c.code));

        if (codes.length > 0) {
          setSearchResults(codes);
          toast.success(`Found ${codes.length} ICD-10 codes from IMO`);
        } else {
          setSearchResults([]);
          toast.error('No codes found. Try a different search term.');
        }
      } catch (error: any) {
        if (!isSubscriptionError(error)) {
          const errorMsg = error?.response?.data?.error || 'Failed to search ICD-10 codes';
          toast.error(errorMsg);
        }
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    } finally {
      searchInFlightRef.current = false;
    }
  };

  const handleApproveCode = async (code: CodeSuggestion) => {
    const note = diagnosticNotes[0];
    if (!note) return;
    if (approveInFlightRef.current) return;

    approveInFlightRef.current = true;
    setApprovingCodeId(code.imoLexicalCode || code.code);
    try {
      await reviewDiagnosticCode(
        appointmentId,
        encounterId,
        isEncounterMode,
        note.id,
        code,
        'approved',
        note.plainText
      );

      // Update diagnostic note with approved code
      setDiagnosticNotes(notes => notes.map(n => {
        if (n.id === note.id) {
          return {
            ...n,
            suggestedCodes: [...(n.suggestedCodes || []), { ...code, status: 'approved' }]
          };
        }
        return n;
      }));

      // AMP-76: selecting a code ends the search. Clear the input and the
      // whole results list so the next search starts from a blank widget.
      setSearchQuery('');
      setSearchResults([]);

      toast.success(`ICD-10 code ${code.code} approved and saved`);
    } catch (error) {
      if (!isSubscriptionError(error)) {
        toast.error('Failed to approve code');
      }
    } finally {
      approveInFlightRef.current = false;
      setApprovingCodeId(null);
    }
  };

  const handleDismissCode = async (code: CodeSuggestion) => {
    // Just remove from search results (don't call backend for dismiss)
    setSearchResults(codes => codes.filter(c =>
      code.imoLexicalCode ? c.imoLexicalCode !== code.imoLexicalCode : c.code !== code.code
    ));
  };

  const handleRemoveApprovedCode = async (code: CodeSuggestion) => {
    const note = diagnosticNotes[0];
    if (!note) return;

    if (!confirm(`Remove approved code ${code.code}?`)) return;

    try {
      await reviewDiagnosticCode(
        appointmentId,
        encounterId,
        isEncounterMode,
        note.id,
        code,
        'rejected',
        note.plainText
      );

      // Update local state
      setDiagnosticNotes(notes => notes.map(n => {
        if (n.id === note.id) {
          return {
            ...n,
            suggestedCodes: (n.suggestedCodes || []).filter(c =>
              code.imoLexicalCode ? c.imoLexicalCode !== code.imoLexicalCode : c.code !== code.code
            )
          };
        }
        return n;
      }));

      toast.success(`ICD-10 code ${code.code} removed`);
    } catch (error) {
      if (!isSubscriptionError(error)) {
        toast.error('Failed to remove code');
      }
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!confirm('Are you sure you want to delete this diagnostic note?')) return;

    // For now, just remove from UI (backend would need a delete endpoint)
    setDiagnosticNotes(notes => notes.filter(n => n.id !== noteId));
    toast.success('Diagnostic note deleted');
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const note = diagnosticNotes[0] ?? null;
  const hasApprovedCodes = Boolean(note?.suggestedCodes?.some(c => c.status === 'approved'));
  const isApproving = approvingCodeId !== null;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Diagnostic Codes</h3>
          <p className="text-sm text-gray-600 mt-1">
            Search for ICD-10 diagnosis codes. Approved codes attach to this encounter.
          </p>
        </div>
      </div>

      {/* Editor surfaces hidden per AMP-29 — last-updated timestamp,
          rich-text editor, dictate button, and dictation modal are
          gated behind DIAGNOSTIC_NOTES_EDITOR_VISIBLE. Search ICD-10
          and approved-codes display continue to render below. */}
      {DIAGNOSTIC_NOTES_EDITOR_VISIBLE && note && (
        <>
          {/* Header */}
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                {note.createdAt && (
                  <span className="text-xs text-gray-500">
                    Last updated: {new Date(note.updatedAt || note.createdAt).toLocaleString()}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Rich Text Editor */}
          <div>
            <RichTextEditor
              initialValue={note.content}
              onSave={async (richText, plainText) => {
                await handleSaveNote(note.id, richText, plainText);
              }}
              placeholder="Type or dictate diagnostic impression..."
              minHeight="150px"
            />
          </div>

          {/* Dictation Button */}
          <div className="flex items-center gap-2 mb-4">
            <button
              onClick={() => setIsDictationOpen({ ...isDictationOpen, [note.id]: true })}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              <MicrophoneIcon className="h-4 w-4 mr-2" />
              Dictate
            </button>
          </div>

          {/* Dictation Recorder */}
          {/* In connected mode appointmentId is undefined; pass encounterId in
              its place. The appointment endpoint route is what we target, and
              the backend service short-circuits on diagnostic-note-* sectionKeys
              so neither findByAppointment nor findByEncounter is consulted. */}
          {isDictationOpen[note.id] && (appointmentId || encounterId) && (
            <QuickDictationRecorder
              isOpen={isDictationOpen[note.id]}
              onClose={() => setIsDictationOpen({ ...isDictationOpen, [note.id]: false })}
              onTranscriptionComplete={(transcription) => handleDictation(note.id, transcription)}
              sectionLabel="Diagnostic Note"
              appointmentId={(appointmentId || encounterId) as string}
              sectionKey={`diagnostic-note-${note.id}`}
            />
          )}
        </>
      )}

      {/* Approved ICD-10 Codes — pinned to the top of the panel, above the
          search widget (AMP-46). Rendered once at least one code has been
          approved against the (lazily created) note record. Stays visible so
          an approval is confirmed without scrolling and remains in view while
          the user continues searching for more codes. */}
      {note && hasApprovedCodes && (
        <div>
          <UnifiedCodeList
            codes={(note.suggestedCodes ?? [])
              .filter(c => c.status === 'approved')
              .map(code => ({
                id: code.imoLexicalCode || code.code,
                code: code.code,
                description: code.description,
                confidence: code.confidence,
                source: code.source as 'imo' | 'imo-precision' | 'llm' | 'ai',
                category: code.category,
                rationale: code.llmRationale,
                status: 'approved' as const,
                codeType: 'icd10' as const
              }))}
            onRemove={(id) => {
              const code = note.suggestedCodes?.find(c => (c.imoLexicalCode || c.code) === id);
              if (code) handleRemoveApprovedCode(code);
            }}
            showApprovedFirst={true}
            emptyMessage="No approved codes"
          />
        </div>
      )}

      {/* ICD-10 Code Search */}
      <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                <h5 className="text-sm font-semibold text-gray-900 mb-3">
                  Search ICD-10 Codes
                </h5>

                <div className="flex gap-2">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearchCodes()}
                    disabled={isApproving}
                    placeholder="Search by diagnosis (e.g., 'chronic low back pain')"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                  <button
                    onClick={handleSearchCodes}
                    disabled={isSearching || isApproving || !searchQuery.trim()}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  >
                    <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
                    {isSearching ? 'Searching...' : 'Search IMO'}
                  </button>
                </div>

                {/* Search Results */}
                {searchResults.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-600 mb-2">
                      Found {searchResults.length} codes from IMO
                    </p>
                    {/* Fixed-height scrollable list  */}
                    <div className="max-h-[480px] overflow-y-auto pr-1">
                      <UnifiedCodeList
                        codes={searchResults.map(code => ({
                          id: code.imoLexicalCode || code.code,
                          code: code.code,
                          description: code.description,
                          confidence: code.confidence,
                          source: code.source as 'imo' | 'imo-precision' | 'llm' | 'ai',
                          category: code.category,
                          rationale: code.llmRationale,
                          status: 'suggested' as const,
                          codeType: 'icd10' as const
                        }))}
                        onApprove={(id) => {
                          const code = searchResults.find(c => (c.imoLexicalCode || c.code) === id);
                          if (code) handleApproveCode(code);
                        }}
                        onReject={(id) => {
                          const code = searchResults.find(c => (c.imoLexicalCode || c.code) === id);
                          if (code) handleDismissCode(code);
                        }}
                        showApprovedFirst={false}
                        emptyMessage="No codes found"
                        approvingId={approvingCodeId}
                        actionsDisabled={isApproving}
                      />
                    </div>
                  </div>
                )}
              </div>
    </div>
  );
};

// Import missing icon
import { MicrophoneIcon } from '@heroicons/react/24/outline';
